<!--
  The page to view and edit an intents

  Props:
  - skill: the skillid of the intent to display
  - intent: the intentid of the intent to display ("new" for new intent)
  - context: the context to use when creating a new intent,
  - view: the version in view (null, "pub", "prod")
  - compare: the version in compare, if any (null, "dev", "pub", "prod")
-->

<template>
  <div>
    <!-- the textarea inputs editor -->
    <el-dialog class="intent-dialog"
      title="Inputs editor"
      :visible="inputs.editor"
      @update:visible="(val) => val || $refs.inputsEditor.handleCancel()">
      <inputs-text-editor ref="inputsEditor" v-if="inputs.editor"
        :inputs="computedInputs.inputs"
        :levels="computedInputs.levels"
        :entities="computedEntities"
        :synonyms-options="itemsOptions.synonym"
        @close="inputs.editor = false"
        @save="handleInputsEditor">
      </inputs-text-editor>
    </el-dialog>
    <!-- the training editor -->
    <el-dialog class="intent-dialog"
      title="Training editor"
      :visible="training.visible"
      @update:visible="handleCloseTraining">
      <!-- the textarea examples editor -->
      <examples-text-editor v-if="training.editor"
        ref="examplesEditor"
        :examples="computedExamples"
        @close="training.editor = false"
        @save="handleExamplesEditor">
      </examples-text-editor>
      <template v-else>
        <div style="float: right">
          <el-button @click="training.editor = true">Editor</el-button>
        </div>
        <el-form
          ref="trainingForm"
          :model="training"
          label-width="150px"
          :hide-required-asterisk="true">
          <h4>Example sentences</h4>
          <p style="margin-top: 0">
            <comparator :view="training.examples" :compare="compareTraining.examples"
              v-for="_ in [0]" :key="_">
              <template v-slot="{ value, other, index, view, compare }">
                <comparator-filter
                  @update:segments="segs => {value.segments = segs.filter(x => x.type === 'slot'); training.update = true}"
                  @delete="handleExampleDelete(value, index)"
                  @valid="v => value.valid = v"
                  :prop="'examples.' + index + '.value'">
                  <template v-slot="{ attrs, listeners }">
                    <example-editor
                      v-bind="attrs"
                      v-on="listeners"
                      :ref="view && 'examples'"
                      v-model="value.value"
                      :active="value.active"
                      @activate="v => activate(training.examples, v ? value.key : null)"
                      placeholder="Example sentence"
                      :slot-suggestions="slotSuggestions"
                      :slot-colors="compare ? compareTraining.colors : null"
                      :new-input="value.temporary">
                    </example-editor>
                  </template>
                </comparator-filter>
              </template>
            </comparator>
          </p>
          <h4 v-show="training.slots.length">Slots</h4>
          <p style="margin-bottom: 0" v-show="training.slots.length">
            <slot-editor header></slot-editor>
            <comparator :view="training.slots" :compare="compareTraining.slots">
              <template v-slot="{ value, other, index, view }">
                <comparator-filter
                  @update:name="v => setSlotName(value, v)"
                  @delete="handleSlotDelete(value, index)"
                  @valid="v => value.valid = v"
                  :prop="'slots.' + index + '.value'"
                  :duplicate-name="slotNameCounts[value.value.name] > 1"
                  :duplicate-entity="!!value.value.entity && slotEntityCounts[value.value.entity] > 1"
                  :deletable="slotNameCounts[value.value.name] > 1">
                  <template v-slot="{ attrs, listeners }">
                    <slot-editor
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="value.value"
                      :other="other && other.value"
                      placeholder="Slot name"
                      :local-variables="localVariables"
                      :style-obj="{backgroundColor: value.cssColor}">
                    </slot-editor>
                  </template>
                </comparator-filter>
              </template>
            </comparator>
          </p>
        </el-form>
      </template>
    </el-dialog>
    <!-- the main form -->
    <el-form
      v-loading="state.loading"
      ref="form"
      :model="viewObj"
      label-width="150px"
      :hide-required-asterisk="true">
      <!-- breadcrumb -->
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item>
          <router-link :to="{ name: 'home', query: {view: view, compare: compare} }">
            Home
          </router-link>
        </el-breadcrumb-item>
        <el-breadcrumb-item>
          <router-link :to="{ name: 'skill', params: {skill: skill}, query: {view: view, compare: compare} }">
            Skill summary
          </router-link>
        </el-breadcrumb-item>
        <el-breadcrumb-item>{{intentId==='new'?'Create intent':'Intent details'}}</el-breadcrumb-item>
      </el-breadcrumb>
      <!-- main title -->
      <main-title
        :skill="skill"
        v-model="viewObj"
        id-field="intent"
        :object-id="intentId"
        :existing="skillObj.data.intents">
        Intent: {{viewObj.name}}
      </main-title>
      <!-- to change the view -->
      <view-selector
        :view="view"
        :compare="compare"
        @update="changeView"
        :object="viewObj"
        :discrete="skillObj.data.discrete_publish"
        :new-item="intentId==='new' || special && !timestamp"
        name="intent"
        @save="save"
        @delete="remove"
        @test="test"
        @untest="untest"
        @publish="publish"
        @unpublish="unpublish"
        @restore="restore"
        ></view-selector>
      <el-row>
        <!-- main options section -->
        <el-col style="padding: 5px;">
          <subtitle id="section-intent">Intent</subtitle>
          <!-- name input -->
          <comparator :view="viewObj" :compare="compareObj"
            prop="name"
            :required="true"
            :rules="AcceptableValidator">
            <template v-slot="{ value, compare, prop, attrs, status }">
              <el-form-item :class="'comparable-' + status"
                label="Intent name"
                :prop="prop"
                v-bind="attrs">
                <el-input
                  v-model="value.name"
                  :disabled="compare || special">
                </el-input>
              </el-form-item>
            </template>
          </comparator>
          <!-- context input -->
          <comparator :view="viewObj" :compare="compareObj"
            v-if="!special"
            prop="context"
            default=""
            :rules="contextRules"
            @rules="val => contextRules = val">
            <template v-slot="{ value, compare, prop, attrs, listeners, status }">
              <el-form-item :class="'comparable-' + status"
                :prop="prop"
                v-bind="attrs">
                <template slot="label">
                  Intent context
                  <tooltip-icon placement="bottom">
                    The <em>intent</em> is available only after this <em>context</em> is created.<br/>
                    Leave empty to be always available.
                  </tooltip-icon>
                </template>
                <context-editor ref="contextEditor"
                  v-model="value.context"
                  placeholder="Gloabl context"
                  :disabled="compare"
                  v-on="listeners"
                  :contexts="contexts"
                  :intentid="intentId"
                  :intents="intentOptions['']">
                </context-editor>
              </el-form-item>
            </template>
          </comparator>
          <!-- domains input -->
          <comparator :view="viewObj" :compare="compareObj"
            v-if="!special"
            prop="domains"
            :default="null"
            :rules="domainsRules">
            <template v-slot="{ value, compare, prop, attrs, status }">
              <el-form-item :class="'comparable-' + status"
                :prop="prop"
                v-bind="attrs">
                <template slot="label">
                  Domains
                  <tooltip-icon placement="bottom">
                    Select in which <em>domains</em> this <em>intent</em> can be called.<br/>
                    Select the box to override the default behavior of the skill.
                  </tooltip-icon>
                </template>
                <domains-selector
                  v-model="value.domains"
                  :disabled="compare"
                  :domains="state.settings.domains"
                  :default-value="skillObj.data.domains || defaultDomains"
                  :rules="domainsRules"
                  @rules="val => domainsRules = val">
                </domains-selector>
              </el-form-item>
            </template>
          </comparator>
          <!-- collapsable for advanced option -->
          <el-collapsable v-if="!special"
            v-model="advanced"
            :forced="viewObj.public || viewObj.unsearchable || viewObj.unsementic || !!viewObj.expected_language">
            <!-- expected languge select -->
            <comparator :view="viewObj" :compare="compareObj"
              prop="expected_language"
              :default="null">
              <template v-slot="{ value, compare, prop, attrs, status }">
                <el-form-item :class="'comparable-' + status"
                  :prop="prop"
                  v-bind="attrs">
                  <template slot="label">
                    Expected language
                    <tooltip-icon placement="bottom">
                      The language the user is expected to talk after this <em>intent</em>.
                    </tooltip-icon>
                  </template>
                  <el-filterable-select
                    :disabled="compare"
                    clearable
                    v-model="value.expected_language"
                    :options="languageOptions">
                  </el-filterable-select>
                </el-form-item>
              </template>
            </comparator>
            <!-- public checkbox -->
            <comparator :view="viewObj" :compare="compareObj"
              prop="public" :default="false" diff>
              <template v-slot="{ value, compare, prop, attrs, status }">
                <el-form-item :class="'comparable-' + status"
                  :prop="prop"
                  v-bind="attrs">
                  <template slot="label">
                    Public
                    <tooltip-icon placement="bottom"
                      :type="value.public ? 'warning' : 'info'">
                      <span class="warning-text">
                        <i class="el-icon-warning"></i> [Advanced usage]<br/>
                      </span>
                      The <em>intent</em> can be called by other <em>skills</em>.
                    </tooltip-icon>
                  </template>
                  <el-checkbox
                  v-model="value.public"
                  :disabled="compare"></el-checkbox>
                </el-form-item>
              </template>
            </comparator>
            <!-- unsearchable checkbox -->
            <comparator :view="viewObj" :compare="compareObj"
              prop="unsearchable" :default="false" diff>
              <template v-slot="{ value, compare, prop, attrs, status }">
                <el-form-item :class="'comparable-' + status"
                  :prop="prop"
                  v-bind="attrs">
                  <template slot="label">
                    Unsearchable
                    <tooltip-icon placement="bottom"
                      :type="value.unsearchable ? 'warning' : 'info'">
                      <span class="warning-text">
                        <i class="el-icon-warning"></i> [Advanced usage]<br/>
                      </span>
                      The <em>intent</em> cannot be searched in elasticsearch.
                    </tooltip-icon>
                  </template>
                  <el-checkbox
                  v-model="value.unsearchable"
                  :disabled="compare"></el-checkbox>
                </el-form-item>
              </template>
            </comparator>
            <!-- unsementic checkbox -->
            <comparator :view="viewObj" :compare="compareObj"
              prop="unsementic" :default="false" diff>
              <template v-slot="{ value, compare, prop, attrs, status }">
                <el-form-item :class="'comparable-' + status"
                  :prop="prop"
                  v-bind="attrs">
                  <template slot="label">
                    Unsementic
                    <tooltip-icon placement="bottom"
                      :type="value.unsementic ? 'warning' : 'info'">
                      <span class="warning-text">
                        <i class="el-icon-warning"></i> [Advanced usage]<br/>
                      </span>
                      The <em>intent</em> won't be indexed on sementic similarity.
                    </tooltip-icon>
                  </template>
                  <el-checkbox
                  v-model="value.unsementic"
                  :disabled="compare"></el-checkbox>
                </el-form-item>
              </template>
            </comparator>
          </el-collapsable>
        </el-col>
        <!-- inputs section -->
        <el-col style="padding: 5px;">
          <subtitle id="section-inputs">Inputs
            <template slot="tooltip">
              Configure the sentences a user can say to trigger this intent. Those inputs can have <em>entities</em> (variables).
            </template>
          </subtitle>
          <!-- language selector -->
          <comparator :view="viewObj" :compare="compareObj"
            prop="language"
            :required="true">
            <template v-slot="{ value, compare, prop, attrs, status }">
              <el-form-item :class="'comparable-' + status"
                label="Language"
                :prop="prop"
                v-bind="attrs">
                <el-filterable-select
                  v-model="value.language"
                  :options="languageOptions"
                  :disabled="compare">
                </el-filterable-select>
                <el-form-errors style="float: right">
                  <el-form-small-item style="display: inline-block; margin-right: 10px"
                    prop="training" :rules="trainingRules">
                    <el-button @click="training.visible = true"
                      :type="trainingValid ? null : 'danger'">Training</el-button>
                  </el-form-small-item>
                  <el-button @click="inputs.editor = true">Editor</el-button>
                </el-form-errors>
              </el-form-item>
            </template>
          </comparator>
          <p>
            <!-- all the inputs -->
            <el-form-sub
              prop="inputs"
              :model="inputs"
              label-width="120px"
              :hide-required-asterisk="true">
              <comparator :view="inputs.list" :compare="compareInputs"
                v-for="_ in [0]" :key="_">
                <template v-slot="{ value, other, index, view }">
                  <comparator-filter
                    @update:segments="segs => {value.segments = segs.filter(x => x.type === 'entity'); entities.update = true}"
                    @update:text="(key, val) => updateEntityName(value.entities[key], val)"
                    @update:ref="(key, val) => updateEntityRef(value.entities[key], val)"
                    @delete="handleInputDelete(value, index)"
                    :prop="'list.'+index+'.value'">
                    <template v-slot="{ attrs, listeners }">
                      <utterance-editor
                        v-bind="attrs"
                        v-on="listeners"
                        :ref="view && 'inputs'"
                        v-model="value.value"
                        :items-options="itemsOptions"
                        :items-links="itemsLinks"
                        :variable-suggestions="localVariables"
                        :new-focus="value.focus"
                        :active="value.active"
                        @activate="v => activate(inputs.list, v ? value.key : null)"
                        placeholder="User expression"
                        :new-input="value.temporary">
                        <span slot="suffix" v-if="!value.temporary"
                          class="el-input__icon intent-level">
                          <el-tooltip placement="top-start" effect="light" :enterable="false" :tabindex="-1">
                            <template slot="content">
                              Default priority for the input
                            </template>
                            <i class="el-icon-circle-close level-clear"
                              @click.stop="value.level = 0"></i>
                          </el-tooltip>
                          <el-tooltip placement="top-start" effect="light" :enterable="false" :tabindex="-1">
                            <template slot="content">
                              Lowest priority for the input<br/>
                              The input is very unlikely to be said
                            </template>
                            <i class="el-icon-bottom level-down"
                              :class="{selected: (value.level || 0) >= 2}"
                              @click.stop="value.level = 2"></i>
                          </el-tooltip>
                          <el-tooltip placement="top-start" effect="light" :enterable="false" :tabindex="-1">
                            <template slot="content">
                              Lower priority for the input<br/>
                              The input is less likely to be said
                            </template>
                            <i class="el-icon-bottom level-down"
                              :class="{selected: (value.level || 0) >= 1}"
                              @click.stop="value.level = 1"></i>
                          </el-tooltip>
                          <i v-if="!value.level"
                            class="el-icon-circle-close placeholder"></i>
                        </span>
                      </utterance-editor>
                    </template>
                  </comparator-filter>
                </template>
              </comparator>
            </el-form-sub>
          </p>
        </el-col>
        <!-- entities section -->
        <el-col style="padding: 5px;">
          <subtitle id="section-entities">Entities
            <template slot="tooltip">
              Configure the expected type of the entities used in your <em>inputs</em>, and the way to get values for the missing ones.
            </template>
          </subtitle>
          <p>
            <el-form-sub
              prop="entities"
              :model="entities"
              label-width="120px"
              :hide-required-asterisk="true">
              <!-- "table" header -->
              <entity-editor header></entity-editor>
              <!-- all entities -->
              <comparator :view="entities.list" :compare="compareEntities">
                <template v-slot="{ value, other, index, view }">
                  <comparator-filter
                    @move="handleEntityMove"
                    group="entities" :index="index"
                    @update:name="val => setEntityName(value, val)"
                    @update:ref="val => setEntityRef(value, val)"
                    @delete="handleEntityDelete(value, index)"
                    :prop="'list.' + index + '.value'"
                    :related="value.related"
                    :duplicate="entities.counts[value.value.name] > 1"
                    :deletable="entities.counts[value.value.name] > entities.maxcounts[value.value.name]">
                    <template v-slot="{ attrs, listeners }">
                      <entity-editor
                        v-bind="attrs"
                        v-on="listeners"
                        v-model="value.value"
                        :other="other && other.value"
                        :active="value.active"
                        @activate="v => activate(entities.list, v ? value.key : null)"
                        :new-focus="value.focus"
                        :entities-options="itemsOptions.entity"
                        :entities-links="itemsLinks.entity"
                        :local-variables="localVariables"
                        :context-variables="contextVariables[viewObj.context]"
                        :new-input="value.temporary">
                      </entity-editor>
                    </template>
                  </comparator-filter>
                </template>
              </comparator>
            </el-form-sub>
          </p>
        </el-col>
        <!-- answers section, similar to inputs -->
        <el-collapse-transition>
          <el-col style="padding: 5px;" v-if="entitiesRequired">
            <subtitle id="section-answers">Answers
              <template slot="tooltip">
                Configure sentences the user can say to fill the missing <em>entities</em>. It cannot be used ot trigger the intents, ony to reply.
              </template>
            </subtitle>
            <p>
              <!-- all the answers -->
              <el-form-sub
                prop="answers"
                :model="answers"
                label-width="120px"
                :hide-required-asterisk="true">
                <comparator :view="answers.list" :compare="compareAnswers"
                  v-for="_ in [0]" :key="_">
                  <template v-slot="{ value, other, index, view }">
                    <comparator-filter
                      @update:segments="segs => {value.segments = segs.filter(x => x.type === 'entity'); entities.update = true}"
                      @update:text="(key, val) => updateEntityName(value.entities[key], val)"
                      @update:ref="(key, val) => updateEntityRef(value.entities[key], val)"
                      @delete="handleAnswerDelete(value, index)"
                      :prop="'list.'+index+'.value'">
                      <template v-slot="{ attrs, listeners }">
                        <utterance-editor
                          v-bind="attrs"
                          v-on="listeners"
                          ref="answers"
                          v-model="value.value"
                          :items-options="itemsOptions"
                          :items-links="itemsLinks"
                          :variable-suggestions="localVariables"
                          :new-focus="value.focus"
                          :active="value.active"
                          @activate="v => activate(answers.list, v ? value.key : null)"
                          placeholder="User answer"
                          :new-input="value.temporary">
                          <template slot="tooltip">
                            Similar to <em>inputs</em>, <em>answers</em> are only matched when replying to a <em>question</em> about a <em>required</em> <em>entity</em>
                          </template>
                        </utterance-editor>
                      </template>
                    </comparator-filter>
                  </template>
                </comparator>
              </el-form-sub>
            </p>
          </el-col>
        </el-collapse-transition>
        <!-- actions section -->
        <el-col style="padding: 5px;">
          <subtitle id="section-actions">Actions
            <template slot="tooltip">
              Configure the actions to perform when the intent is matched. This is where you reply to the user.
            </template>
          </subtitle>
          <p>
            <!-- dropdown to add a new action -->
            <el-dropdown @command="addAction"
              placement="bottom-end">
              <el-button>
                Add new Action
                <i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="message"
                  icon="el-icon-chat-line-square">Add Text response
                  <tooltip-icon placement="right">
                    A text response that will be read by the speech synthesizer.
                  </tooltip-icon>
                </el-dropdown-item>
                <el-dropdown-item command="audio"
                  icon="el-icon-microphone">Add Audio response
                  <tooltip-icon placement="right">
                    An audio file to read to the user.
                  </tooltip-icon>
                </el-dropdown-item>
                <el-dropdown-item command="image"
                  icon="el-icon-picture-outline">Add Image response
                  <tooltip-icon placement="right">
                    An image file to display to the user.
                  </tooltip-icon>
                </el-dropdown-item>
                <el-dropdown-item command="script"
                  icon="el-icon-cpu">Add Script
                  <el-tooltip placement="right" effect="light" :tabindex="-1">
                    <template slot="content">
                      A script to execute, in order to compute advanced values.
                    </template>
                    <span class="warning-text">
                      [Advanced usage] <i class="el-icon-warning"></i>
                    </span>
                  </el-tooltip>
                </el-dropdown-item>
                <el-dropdown-item command="api"
                  icon="el-icon-guide">Add API call
                  <el-tooltip placement="right" effect="light" :tabindex="-1">
                    <template slot="content">
                      An API call, in order to retreave data.
                    </template>
                    <span class="warning-text">
                      [Advanced usage] <i class="el-icon-warning"></i>
                    </span>
                  </el-tooltip>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </p>
          <!-- all the actions -->
          <comparator prop="value" :view="actions.list" :compare="compareActions"
            v-for="_ in [0]" :key="_">
            <template v-slot="{ value, other, index, view: _view, compare, status }">
              <comparator-filter
                @move="handleActionMove"
                @delete="handleActionDelete(value.key)"
                group="actions">
                <template v-slot="{ attrs, listeners }">
                  <text-action-editor v-if="value.value.type === 'message'"
                    :class="'comparable-' + status"
                    v-on="listeners"
                    v-bind="attrs"
                    v-model="value.value"
                    :index="index"
                    :active="value.active"
                    @activate="v => activate(actions.list, v ? value.key : null)"
                    :new-focus="value.focus"
                    :language-options="languageOptions"
                    :local-variables="localVariables"
                    :context-variables="contextVariables[viewObj.context]"
                    :prop="_view && 'actions.'+index"
                    :disabled="compare"></text-action-editor>
                  <urls-action-editor v-else-if="value.value.type === 'audio' || value.value.type === 'image'"
                    :class="'comparable-' + status"
                    v-on="listeners"
                    v-bind="attrs"
                    v-model="value.value"
                    :index="index"
                    :active="value.active"
                    @activate="v => activate(actions.list, v ? value.key : null)"
                    :new-focus="value.focus"
                    :language-options="languageOptions"
                    :local-variables="localVariables"
                    :context-variables="contextVariables[viewObj.context]"
                    :prop="_view && 'actions.'+index"
                    :placeholder="value.value.type + ' file url'"
                    :disabled="compare"></urls-action-editor>
                  <orderable-box v-else-if="value.value.type === 'script'"
                    :class="'comparable-' + status"
                    v-on="listeners"
                    v-bind="attrs"
                    :index="index"
                    :active="value.active"
                    @activate="v => actions.active = v ? value.key : null"
                    icon="el-icon-cpu">
                    <template slot="content">
                      Script
                      <tooltip-icon placement="top">
                        Edit the <em>local variables </em><code>@variable</code> in order to use them<br/>
                        in the <em>actions</em>, the <em>output contexts</em> or the <em>followup conditions</em>.
                      </tooltip-icon>
                    </template>
                    <el-form-item-code-input
                      type="program"
                      v-model="value.value.script"
                      :prop="_view && 'actions.'+index+'.script'"
                      :required="_view"
                      field="script"
                      :local-variables="localVariables"
                      :context-variables="contextVariables[viewObj.context]"
                      :autosize="{minRows: 5, maxRows: 20}"
                      :disabled="compare"></el-form-item-code-input>
                  </orderable-box>
                  <api-action-editor v-else-if="value.value.type === 'api'"
                    :class="'comparable-' + status"
                    v-on="listeners"
                    v-bind="attrs"
                    v-model="value.value"
                    :index="index"
                    :active="value.active"
                    @activate="v => actions.active = v ? value.key : null"
                    :new-focus="value.focus"
                    :prop="_view && 'actions.'+index"
                    :required="_view"
                    :skill="skill"
                    :view="view"
                    :local-variables="localVariables"
                    :context-variables="contextVariables[viewObj.context]"
                    :api-options="itemsOptions.apis"
                    :disabled="compare">
                  </api-action-editor>
                </template>
              </comparator-filter>
            </template>
          </comparator>
        </el-col>
        <!-- output contexts section -->
        <el-col style="padding: 5px;">
          <subtitle id="section-outputs">Output contexts
            <template slot="tooltip">
              Create and configure contexts. Creating a context makes the <em>intents</em> in that context available to the user.
            </template>
          </subtitle>
          <!-- name input -->
          <comparator :view="viewObj" :compare="compareObj"
            prop="turns" diff
            @input="(v) => $set(viewObj, 'turns', v ? 0 : 1)">
            <template v-slot="{ value, compare, prop, listeners, status }">
              <el-form-item :class="'comparable-' + status"
                :prop="prop">
                <template slot="label">
                  Help intent
                  <tooltip-icon placement="bottom">
                    An <em>help intent</em> does not count as an interaction<br/> and does not decrease the <em>TTL</em> of existing <em>contexts</em>.<br/>
                    It is useful to avoid cleaning the <em>contexts</em> when the user is just lost.
                  </tooltip-icon>
                </template>
                <el-checkbox
                  :value="value.turns === 0"
                  v-on="listeners"
                  :disabled="compare || special">
                </el-checkbox>
              </el-form-item>
            </template>
          </comparator>
          <p>
            <el-form-sub
              prop="outputs"
              :model="outputs"
              label-width="120px"
              :hide-required-asterisk="true">
              <!-- "table" header -->
              <output-context-editor header></output-context-editor>
              <!-- all output contexts -->
              <comparator :view="outputs.list" :compare="compareOutputs">
                <template v-slot="{ value, other, index, view }">
                  <comparator-filter
                    @move="handleOutputMove"
                    @delete="handleOutputDelete(value.key)"
                    group="outputs" :index="index"
                    :prop="'list.'+index+'.value'"
                    :rules="outputs.duplicateValidator"
                    :typeRules="outputs.exclusiveValidator[index > outputs.exclusiveIndex]"
                    :ttlRules="outputs.ttl1Validator[index > outputs.exclusiveIndex]">
                    <template v-slot="{ attrs, listeners }">
                      <output-context-editor
                        v-bind="attrs"
                        v-on="listeners"
                        v-model="value.value"
                        :other="other && other.value"
                        :contexts="contexts"
                        :intentid="intentId"
                        :intents="intentOptions['']"
                        placeholder="Choose an output context"
                        :active="value.active"
                        @activate="v => activate(outputs.list, v ? value.key : null)"
                        group="outputs" :index="index"
                        :local-variables="localVariables"
                        :context-variables="contextVariables[viewObj.context]"
                        :variable-suggestions="contextVariables[value.value.context]"
                        :new-input="value.temporary">
                      </output-context-editor>
                    </template>
                  </comparator-filter>
                </template>
              </comparator>
            </el-form-sub>
          </p>
        </el-col>
        <!-- follow intents section -->
        <el-col style="padding: 5px;">
          <subtitle id="section-follow">Following intent
            <template slot="tooltip">
              Conditionally trigger another intent. This is the way to do conditional branching, or to reuse intents.
            </template>
          </subtitle>
          <!-- all the follow intent -->
          <el-form-sub
            prop="follow"
            :model="follow"
            label-width="120px"
            :hide-required-asterisk="true">
            <comparator :view="follow.list" :compare="compareFollow">
              <template v-slot="{ value, other, index, view }">
                <comparator-filter
                  @move="handleFollowMove"
                  @delete="handleFollowDelete(value.key)"
                  group="follow" :index="index"
                  :prop="view && 'list.' + index + '.value'">
                  <template v-slot="{ attrs, listeners }">
                    <follow-intent-editor
                      v-bind="attrs"
                      v-on="listeners"
                      v-model="value.value"
                      :other="other && other.value"
                      :active="value.active"
                      @activate="v => activate(follow.list, v ? value.key : null)"
                      :last="index === follow.list.length - 1"
                      :special-skills="state.settings.special_skills"
                      :skill-options="skillOptions"
                      :intent-options="intentOptions"
                      :local-variables="localVariables"
                      :context-variables="contextVariables[viewObj.context]"
                      :variable-suggestions="intentVariables">
                    </follow-intent-editor>
                  </template>
                </comparator-filter>
              </template>
            </comparator>
          </el-form-sub>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<script>
import State from '../../stores/state.js'
import { AcceptableValidator } from '../../utils/validators'
import { int2color } from '../../utils/utils'
import { allSlots } from '../element/ElExampleInput'

import InputsTextEditor from './InputsTextEditor.vue'
import ExampleEditor from './ExampleEditor.vue'
import SlotEditor from './SlotEditor.vue'
import ExamplesTextEditor from './ExamplesTextEditor.vue'
import ViewSelector from '../ViewSelector.vue'
import UtteranceEditor from './UtteranceEditor.vue'
import ContextEditor from './ContextEditor.vue'
import ElFilterableSelect from '../element/ElFilterableSelect.vue'
import OutputContextEditor from './OutputContextEditor.vue'
import TextActionEditor from './TextActionEditor.vue'
import UrlsActionEditor from './UrlsActionEditor.vue'
import ApiActionEditor from './ApiActionEditor.vue'
import OrderableBox from '../orderable/OrderableBox.vue'
import ElFormItemCodeInput from '../element/ElFormItemCodeInput.vue'
import FollowIntentEditor from './FollowIntentEditor.vue'
import EntityEditor from './EntityEditor.vue'
import ElCollapsable from '../element/ElCollapsable.vue'
import { DomainsSelector, defaultDomains } from '../DomainsSelector.vue'

export default {
  name: 'IntentPage',
  components: {
    InputsTextEditor,
    ExampleEditor,
    SlotEditor,
    ExamplesTextEditor,
    ViewSelector,
    UtteranceEditor,
    ContextEditor,
    ElFilterableSelect,
    OutputContextEditor,
    TextActionEditor,
    UrlsActionEditor,
    ApiActionEditor,
    OrderableBox,
    ElFormItemCodeInput,
    FollowIntentEditor,
    EntityEditor,
    ElCollapsable,
    DomainsSelector,
  },
  props: {
    skill: String,
    intent: String,
    context: String,
    view: String,
    compare: String,
  },
  data () {
    return {
      state: State.state,
      inputs: { // for input section
        list: [], // a list of input objects, with keys
        /* ex: [{
                  key: '0.4538979', // randomly generated
                  value: '(#Hello:synonymid|) my name is @name:entityid',
                  segments: [...], // see UtteranceEditor.segments
                  entities: { // entity object by segment id
                    'segmentid': {...} // see entities.list
                  },
                  counts: { // how many times each variable is used
                    'name': 1, // duplicate error if > 1
                  },
                }, ...]
        */
        editor: false, // to open the text inputs editor
      },
      maxInputLevel: 2,
      training: {
        visible: false, // if the dialog should be opened
        // if should have a global update of all examples and slots
        // the update is delayed in "$nextTick" to avoid multiple calls
        update: false,
        examples: [], // a list of all examples
        /* ex: [{
                  key: '0.4538979', // randomly generated
                  value: 'I said {@slotName:slot text}',
                  segments: [...], // see ExampleEditor.segments
                  slots: { // slots object by segment id
                    'slotsName': {...} // see training.slots
                  },
                  valid: true, // if the particular example is valid
                }, ...]
        */
        slots: [], // a list of all slots
        /* ex: [{
                  key: '0.4538979', // randomly generated
                  value: {
                    name: 'slotName', // name of the slot
                    entity: 'entityName', // name of the entity it's related to
                    key: 'entityid', // key of the entity it's related to
                  },
                  keys: { // precisely with segment is using this slot
                    'exampleid': segmentid,
                  },
                  valid: true, // if the particular slot is valid
                  color: 0, // the color index of the slot
                  cssColor: '#AABBCC', the css representation of the color
                }, ...]
        */
        editor: false, // to open the text examples editor
      },
      entities: { // for entities section
        list: [], // a list of entity objects, with keys
        /* ex: [{
                  key: '0.4538979', // randomly generated
                  value: {...}, // see EntityEditor
                  // if this entity has been create automatically or manually
                  auto: false,
                  // if this entity is used by an input or answer
                  related: true,
                  // the number of time it is used by inputs and answers
                  count: 2,
                  keys: { // precisely with segment is using this entity
                    'inputid': 'segmentid',
                    ...
                  },
                }, ...]
        */
        // if should have a global update of all entities
        // the update is delayed in "$nextTick" to avoid multiple calls
        update: false,
        // how many entities with a specific name
        // duplicate error if > 1
        counts: {}, // {'name': 1}
        // how many times (at most) one input is using an entity name
        // duplicate error if > 1
        maxcounts: {}, // {'name': 1}
      },
      answers: { // for answers section, same as inputs
        list: [],
      },
      outputs: { // output contexts section
        // how many outputs with the same context
        // duplicate error if > 1
        counts: {}, // {'context': 1}
        // a list of output objects, with keys
        // see OutputcontextEditor for the value
        list: [],
        duplicateValidator: { // validator for duplicate context
          validator: (rule, value, callback) => callback(
            this.outputs.counts[value] > 1
              ? new Error('duplicate output context') : undefined),
          trigger: 'input',
        },
        exclusiveValidator: { // validator for duplicate exclusivity
          true: { // is after exclusiveIndex
            validator: (rule, value, callback) => callback(value.exclusive
              ? new Error('maximum one exclusive output context') : undefined),
            trigger: 'input',
          },
          false: { // is before exclusiveIndex
            validator: (rule, value, callback) => callback(),
            trigger: 'input',
          },
        },
        ttl1Validator: { // validator for ttl of 1 after an exclusive context
          true: { // is after exclusiveIndex
            validator: (rule, value, callback) => callback(
              value.ttl === 1 && !value.exclusive
                ? new Error('a temporary context of TTL 1 cannot survive ' +
                'after an exclusive context') : undefined),
            trigger: 'input',
          },
          false: { // is before exclusiveIndex
            validator: (rule, value, callback) => callback(),
            trigger: 'input',
          },
        },
        // the index of the first exclusive context
        // used to activate exclusiveValidator and ttl1Validator
        exclusiveIndex: 0,
      },
      actions: { // for actions section
        list: [], // a list of action objects, with keys
        /* ex: [{
                  key: '0.4538979', // randomly generated
                  focus: true, // if should get focus when created
                  value : {
                    type: 'message' || 'audio' || 'image' || 'script' || 'api',
                    ... // see addAction
                  }
                }]
        */
      },
      follow: { // for follow section
        // a list of follow objects, with keys
        // see FollowIntentEditor for the value
        list: [{ // start with an empty follow
          key: Math.random() + '',
          value: {
            condition: '',
            skill: '',
            intent: '',
            variables: {},
          },
        }],
      },
      skillObj: State.empty, // the view skill data
      viewObj: { // the view intent data
        inputs: [],
        input_levels: {},
        entities: [],
        answers: [],
        actions: [],
        outputs: [],
        follow: [],
      },
      compareObj: undefined, // the compare intent data
      intentId: null, // the intentid ("new" for new intent)
      advanced: false, // to open the advance slidedown
      // the available languages
      languageOptions: [{ value: 'vi-VN' }, { value: 'en-US' }],
      AcceptableValidator, // validator for the name
      // rules for the context, filled with @rules event of ContextEditor
      contextRules: [],
      domainsRules: [],
      defaultDomains,
    }
  },
  computed: {
    // various fields from the intent
    status () { return this.viewObj.status || null },
    timestamp () { return this.viewObj.timestamp || null },
    published () { return this.viewObj.published || null },
    approved () { return this.viewObj.approved || null },
    // if the intent is special
    special () {
      return this.viewObj.intent
        ? this.viewObj.intent.length !== 32 : false
    },
    // the seen variables for each context
    contextVariables () {
      let out = {}
      for (let context of this.skillObj.data.contexts) {
        out[context.context] = context.variables.map(variable => {
          return { value: variable.name, count: variable.count }
        })
      }
      return out
    },
    // the seen local variables
    localVariables () {
      return this.entities.list.filter(x => !x.temporary).map(entity => {
        return { value: entity.value.name, count: entity.count }
      })
    },
    // if any entity is required (to activate answers)
    entitiesRequired () {
      for (let entity of this.entities.list) {
        if (entity.value.required) return true
      }
      return false
    },
    // transform various lists of objects to list of values
    computedInputs () {
      const list = this.inputs.list.filter(x => !x.temporary)
      let levels = {}
      list.forEach((x, i) => {
        if (x.level) levels[i + ''] = x.level
      })
      return {
        inputs: list.map(x => x.value),
        levels: levels,
      }
    },
    computedAnswers () { return this.answers.list.filter(x => !x.temporary).map(x => x.value) },
    computedOutputs () { return this.outputs.list.filter(x => !x.temporary).map(x => x.value) },
    computedActions () { return this.actions.list.map(x => x.value) },
    computedEntities () { return this.entities.list.filter(x => !x.temporary).map(x => x.value) },
    computedExamples () { return this.training.examples.filter(x => !x.temporary).map(x => x.value) },
    computedSlots () {
      let slots = {}
      for (let slot of this.training.slots) {
        if (slot.value.entity) {
          slots[slot.value.name] = slot.value.entity
        }
      }
      return slots
    },
    computedTraining () {
      if (!this.computedExamples.length) return null
      return {
        examples: this.computedExamples,
        slots: this.computedSlots,
      }
    },
    // all the available intents by skill ('' for this skill)
    intentOptions () {
      let allIntents = {}
      for (let intent of this.skillObj.data.intents) {
        allIntents[intent.intent] = intent
      }
      let intents = this.skillObj.data.intents.map(intent => {
        let followup = intent.context &&
          intent.context.substr(0, 'followup:'.length) === 'followup:'
          ? intent.context.substr('followup:'.length) : null
        let contextName = null
        if (followup) contextName = '⮡' + allIntents[followup].name
        else if (intent.context) contextName = intent.context
        return {
          value: intent.intent,
          name: intent.name,
          context: intent.context,
          followup: followup,
          contextName: contextName,
        }
      })
      intents.sort((a, b) =>
        a.context === b.context ? (a.name > b.name) - (a.name < b.name)
          : !b.context - !a.context || !b.followup - !a.followup ||
          (a.contextName > b.contextName) - (a.contextName < b.contextName))
      let out = { '': intents }
      for (let skill of this.skillObj.data.public) {
        intents = skill.intents.map(intent => {
          return {
            value: intent.intent,
            name: intent.name,
          }
        })
        intents.sort((a, b) => (a.name > b.name) - (a.name < b.name))
        out[skill.skill] = intents
      }
      return out
    },
    // all the available public skills
    skillOptions () {
      let out = this.skillObj.data.public.map(skill => {
        return {
          value: skill.skill,
          name: skill.name,
        }
      })
      out.sort((a, b) => (a.context > b.context) -
        (a.context < b.context) || (a.name > b.name) - (a.name < b.name))
      return out
    },
    // list of all seen contexts
    contexts () {
      return this.skillObj.data.contexts.filter(x => {
        return x.context &&
        x.context.substr(0, 'followup:'.length) !== 'followup:'
      })
    },
    // all available entities and synonyms
    itemsOptions () {
      const sort = (a, b) => (a.name > b.name) - (a.name < b.name) || b.local - a.local
      const sorted = l => {
        l = [...l]
        l.sort(sort)
        return l
      }
      return {
        entity: sorted(this.skillObj.data.entities),
        synonym: sorted(this.skillObj.data.synonyms),
        apis: sorted(this.skillObj.data.apis),
      }
    },
    // all the current slots
    slotSuggestions () {
      let byName = {}
      let options = []
      for (let slot of this.training.slots) {
        if (slot.value.name in byName) continue
        let opt = {
          key: slot.key,
          value: slot.value.name,
          style: {
            'backgroundColor': slot.cssColor,
          },
          used: false,
        }
        byName[slot.value.name] = opt
        options.push(opt)
      }
      options.sort((a, b) => (a.value > b.value) - (a.value < b.value))
      for (let ex of this.training.examples.filter(x => !x.temporary)) {
        for (let seg of ex.segments || []) {
          let opt = byName[seg.name]
          if (!opt) continue
          else if (opt.used) opt.used = true
          else opt.used = seg.key
        }
      }
      return options
    },
    // the number of slots per name
    slotNameCounts () {
      let counts = {}
      for (let slot of this.training.slots) {
        counts[slot.value.name] = (counts[slot.value.name] || 0) + 1
      }
      return counts
    },
    // the number of slots per entity
    slotEntityCounts () {
      let counts = {}
      for (let slot of this.training.slots) {
        if (slot.value.entity) {
          counts[slot.value.entity] = (counts[slot.value.entity] || 0) + 1
        }
      }
      return counts
    },
    // if all he training is valid
    trainingRules () {
      return {
        validator: (rule, value, callback) => this.validateTraining(callback),
      }
    },
    trainingValid () {
      return this.training.examples.every(x => x.valid || x.temporary) &&
        this.training.slots.every(x => x.valid || x.temporary)
    },
    // the value of the temporary input
    temporaryInput () {
      let x = this.inputs.list.find(x => x.temporary)
      return x && x.value || ''
    },
    // the value of the temporary input
    temporaryAnswer () {
      let x = this.answers.list.find(x => x.temporary)
      return x && x.value || ''
    },
    // the value of the temporary input
    temporaryEntity () {
      let x = this.entities.list.find(x => x.temporary)
      return x && x.value.name || ''
    },
    // the value of the temporary input
    temporaryOutput () {
      let x = this.outputs.list.find(x => x.temporary)
      return x && x.value.context || ''
    },
    // the value of the temporary example
    temporaryExample () {
      let x = this.training.examples.find(x => x.temporary)
      return x && x.value || ''
    },
    // router-link to accesisble entities and synonyms
    itemsLinks () {
      let admin = this.state.user && this.state.user.role === 'admin'
      let build = (items, type) => {
        let out = {}
        for (let item of items) {
          if (item.local) {
            out[item[type]] = {
              name: type,
              params: {
                skill: this.skill,
                [type]: item[type],
              },
              query: this.$route.query,
            }
          } else if (admin) {
            out[item[type]] = {
              name: type,
              params: {
                skill: item.skill || 'global',
                [type]: item[type],
              },
              query: { view: 'prod' },
            }
          }
        }
        return out
      }
      return {
        entity: build(this.skillObj.data.entities, 'entity'),
        synonym: build(this.skillObj.data.synonyms, 'synonym'),
      }
    },
    // all the seen intent variables
    intentVariables () {
      let out = { '': {} }
      for (let intent of this.skillObj.data.intents) {
        out[''][intent.intent] = intent.variables.map(variable => {
          return { value: variable.name, count: variable.count }
        })
      }
      for (let skill of this.skillObj.data.public) {
        out[skill.skill] = {}
        for (let intent of skill.intents) {
          out[skill.skill][intent.intent] = intent.variables.map(variable => {
            return { value: variable.name, count: variable.count }
          })
        }
      }
      return out
    },
    // turns various lists of values into lists of objects with key
    compareInputs () {
      if (!this.compareObj) return undefined
      return this.compareObj.inputs.map(val => {
        return {
          key: Math.random() + '',
          value: val,
        }
      })
    },
    compareAnswers () {
      if (!this.compareObj) return undefined
      return this.compareObj.answers.map(val => {
        return {
          key: Math.random() + '',
          value: val,
        }
      })
    },
    compareEntities () {
      if (!this.compareObj) return undefined
      return this.compareObj.entities.map(entity => {
        return {
          key: Math.random() + '',
          value: JSON.parse(JSON.stringify(entity)),
          auto: false,
        }
      })
    },
    compareActions () {
      if (!this.compareObj) return undefined
      return this.compareObj.actions.map((val) => {
        return {
          key: Math.random() + '',
          value: val,
        }
      })
    },
    compareOutputs () {
      if (!this.compareObj) return undefined
      return this.compareObj.outputs.map(val => {
        return {
          key: Math.random() + '',
          value: val,
        }
      })
    },
    compareFollow () {
      if (!this.compareObj) return undefined
      return this.compareObj.follow.map(val => {
        return {
          key: Math.random() + '',
          value: val,
        }
      })
    },
    compareTraining () {
      if (!this.compareObj) {
        return {
          examples: undefined,
          slots: undefined,
          colors: undefined,
        }
      }
      if (!this.compareObj.training) {
        return {
          examples: [],
          slots: [],
          colors: {},
        }
      }

      let colorByName = {}
      let seenColors = {}
      for (let slot of this.training.slots) {
        seenColors[slot.color] = true
        if (!(slot.value.name in colorByName)) {
          colorByName[slot.value.name] = slot.cssColor
        }
      }

      let seenSlots = {}
      let slots = []
      for (let name in this.compareObj.training.slots || {}) {
        seenSlots[name] = true
        slots.push({
          key: Math.random() + '',
          value: {
            name: name,
            entity: this.compareObj.training.slots[name] || '',
          },
          cssColor: colorByName[name],
        })
      }

      let examples = (this.compareObj.training.examples || []).map(ex => {
        for (let name of allSlots(ex)) {
          if (!seenSlots[name]) {
            seenSlots[name] = true
            slots.push({
              key: Math.random() + '',
              value: {
                name: name,
                entity: '',
              },
              cssColor: colorByName[name],
            })
          }
        }
        return {
          key: Math.random() + '',
          value: ex,
        }
      })

      slots.sort((a, b) => (a.value.name > b.value.name) - (a.value.name < b.value.name))
      let color = 0
      let colors = {}
      for (let slot of slots) {
        if (!slot.cssColor) {
          while (seenColors[color]) color++
          slot.cssColor = color
          color++
        }
        colors[slot.value.name] = slot.cssColor
      }

      return {
        examples: examples,
        slots: slots,
        colors: colors,
      }
    },
  },
  // to display errors
  inject: ['onerror'],
  // get all the objects
  created () {
    this.intentId = this.intent
    Promise.all([
      this.loadSkill(),
      this.loadView(),
      this.loadCompare(),
    ]).finally(() => {
      this.$nextTick(() => {
        this.$anchorsLoaded()
        this.checkHash()
      })
    })
  },
  watch: {
    // user changed, relaod objects
    'state.user' () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // skill changed, reload objects
    skill () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // intent changed, reload intent objects
    intent () {
      // special case, happens when a new intent is saved
      // and the created itentid is set
      if (this.intentId === this.intent) return
      // reload the intent objects
      this.intentId = this.intent
      this.loadView()
      this.loadCompare()
    },
    // view changed, reload the view objects
    view () {
      this.loadSkill()
      this.loadView()
    },
    // compare changed, reload the compare object
    compare () {
      this.loadCompare()
    },
    // general things to update when the view changes
    viewObj (val) {
      this.updateInputs(val.inputs, val.input_levels)
      this.updateAnswers(val.answers)
      this.updateOutputs(val.outputs)
      this.updateActions(val.actions)
      this.updateFollow(val.follow)
      this.updateEntities(val.entities)
      this.updateTraining(val.training)
    },
    // synchronize viewObj.inputs and inputs.list
    'viewObj.inputs' (val) { this.updateInputs(val, this.viewObj.input_levels) },
    'viewObj.input_levels' (val) { this.updateInputs(this.viewObj.inputs, val) },
    computedInputs (val) {
      this.$set(this.viewObj, 'inputs', val.inputs)
      this.$set(this.viewObj, 'input_levels', val.levels)
    },
    // synchronize viewObj.answers and answers.list
    'viewObj.answers' (val) { this.updateAnswers(val) },
    computedAnswers (val) { this.$set(this.viewObj, 'answers', val) },
    // synchronize viewObj.outputs and outputs.list
    'viewObj.outputs' (val) { this.updateOutputs(val) },
    computedOutputs (val) { this.$set(this.viewObj, 'outputs', val) },
    // update outputs.counts and outputs.exclusiveIndex
    'outputs.list': {
      handler (list) {
        list = list.filter(x => !x.temporary)
        let counts = {}, exclusiveIndex = list.length
        list.forEach((item, index) => {
          counts[item.value.context] = (counts[item.value.context] || 0) + 1
          if (item.value.exclusive) {
            exclusiveIndex = Math.min(index, exclusiveIndex)
          }
        })
        this.outputs.counts = counts
        this.outputs.exclusiveIndex = exclusiveIndex
      },
      deep: true,
    },
    // synchronize viewObj.actions and actions.list
    'viewObj.actions' (val) { this.updateActions(val) },
    computedActions (val) { this.$set(this.viewObj, 'actions', val) },
    // synchronize viewObj.follow and follow.list
    'viewObj.follow' (val) { this.updateFollow(val) },
    'follow.list': {
      handler (list) {
        list = list.filter(x => !x.temporary)
        this.viewObj.follow = list.map(v => v.value)
        // remove the last follow if it's empty
        let last = list[list.length - 1].value
        if (!last.condition && !last.skill && !last.intent &&
          !last.exit && !last.speech) {
          this.viewObj.follow.pop()
        }
      },
      deep: true,
    },
    // synchronize viewObj.entities and entities.list
    'viewObj.entities' (val) { this.updateEntities(val) },
    computedEntities (val) { this.$set(this.viewObj, 'entities', val) },
    // defer updateSegmentEntities to group all the calls
    'entities.update' (val) {
      if (!val) return
      let next = () => {
        this.entities.update = false
        this.updateSegmentEntities()
        if (this.entities.update) this.$nextTick(next)
      }
      this.$nextTick(next)
    },
    '$route.hash' () { this.checkHash() },
    'inputs.editor' (val) {
      if (val) {
        if (this.$route.hash !== '#inputs-editor') {
          this.$router.replace({ query: this.$route.query, hash: '#inputs-editor' })
        }
      } else if (this.$route.hash === '#inputs-editor') {
        this.$router.replace({ query: this.$route.query, hash: null })
      }
    },
    'training.visible' () { this.handleTrainingHash() },
    'training.editor' () { this.handleTrainingHash() },
    // synchronize viewObj.entities and entities.list
    'viewObj.training' (val) { this.updateTraining(val) },
    computedTraining (val) {
      if (val) {
        this.$set(this.viewObj, 'training', val)
      } else {
        this.$delete(this.viewObj, 'training')
      }
    },
    // defer updateSegmentEntities to group all the calls
    'training.update' (val) {
      if (!val) return
      let next = () => {
        this.training.update = false
        this.updateSegmentSlots()
        if (this.training.update) this.$nextTick(next)
      }
      this.$nextTick(next)
    },
    'entities.list': {
      handler () { this.updateSlotsEntities() },
      deep: true,
    },
    'training.slots': {
      handler () { this.updateSlotsEntities() },
      deep: true,
    },
    temporaryInput (val) {
      if (!val) return
      let x = this.inputs.list.find(x => x.temporary)
      x.temporary = false
      this.inputs.list.push(this.getNewUtterance())
    },
    temporaryAnswer (val) {
      if (!val) return
      let x = this.answers.list.find(x => x.temporary)
      x.temporary = false
      this.answers.list.push(this.getNewUtterance())
    },
    temporaryEntity (val) {
      if (!val) return
      let x = this.entities.list.find(x => x.temporary)
      x.temporary = false
      this.entities.list.push(this.getNewEntity())
    },
    temporaryOutput (val) {
      if (!val) return
      let x = this.outputs.list.find(x => x.temporary)
      x.temporary = false
      this.outputs.list.push(this.getNewOutput())
    },
    temporaryExample (val) {
      if (!val) return
      let x = this.training.examples.find(x => x.temporary)
      x.temporary = false
      this.training.examples.push(this.getNewExample())
    },
  },
  methods: {
    // load the skill object form the API
    loadSkill () {
      let skill = this.skill, view = this.view
      return State.getSkill(skill, view).then(data => {
        if (skill !== this.skill || view !== this.view) return
        this.skillObj = data
      }).catch(this.onerror)
    },
    // load the view intent object form the API
    loadView () {
      // a new item, create it empty
      if (this.intentId === 'new') {
        this.viewObj = {
          name: '',
          context: this.context === 'global' ? null : this.context || null,
          public: false,
          unsearchable: false,
          unsementic: false,
          language: 'vi-VN',
          inputs: [],
          input_levels: {},
          entities: [],
          answers: [],
          actions: [],
          outputs: [],
          follow: [],
        }
        return Promise.resolve()
      // else laod it
      } else {
        let skill = this.skill, intent = this.intentId, view = this.view
        return State.getIntent(skill, intent, view).then(data => {
          if (skill !== this.skill || intent !== this.intentId ||
            view !== this.view) return
          this.viewObj = data
        }, onerror)
      }
    },
    // load the compare intent object form the API
    loadCompare () {
      if (this.intentId === 'new' || !this.compare) {
        this.compareObj = undefined
        return Promise.resolve()
      } else {
        let skill = this.skill, intent = this.intentId, view = this.compare
        return State.getIntent(skill, intent, view).then(data => {
          if (skill !== this.skill || intent !== this.intentId ||
            view !== this.compare) return
          this.compareObj = data
        }, onerror)
      }
    },

    activate (list, key) {
      list.forEach(value => {
        if (value.key === key) {
          if (!value.active) value.active = true
        } else {
          if (value.active) value.active = false
        }
      })
    },

    // updates entities from viewObj.entities
    updateEntities (entities) {
      // check if entities are different
      let same = !!this.entities.list.length
      same = same && this.computedEntities.length === entities.length
      if (same) {
        for (let i = 0; i < entities.length; i++) {
          if (entities[i] !== this.computedEntities[i]) {
            same = false
            break
          }
        }
      }
      // create a new entity list
      if (!same) {
        this.entities.list = entities.map(entity => {
          return {
            // a unique key for v-for
            key: Math.random() + '',
            // the real value
            value: JSON.parse(JSON.stringify(entity)),
            // if the entity was automatically created
            // null will be changed by updateSegmentEntities
            auto: null,
            // if the entity is associated to any segment
            related: false,
            // the number of time it is associated to a segment
            count: 0,
            // the keys of all the associated segments
            keys: {},
            // if should be opened
            active: false,
          }
        })
        this.entities.list.push(this.getNewEntity())
        // let updateSegmentEntities finish the job
        this.entities.update = true
      }
    },
    // updates all the specific fields of inputs, answers and entities
    updateSegmentEntities () {
      // a list of all entities for each name
      let entities = {}
      // a set of entities that should change if not matching segment
      let unrelated = {}
      // how many entities for each name
      let counts = {}
      // which entities still exist
      let seen = {}

      for (let entity of this.entities.list.filter(x => !x.temporary)) {
        seen[entity.key] = true
        // the number of time an entity is refrenced
        entity.count = 0
        // the segment associated for each utterance
        entity.keys = {}

        if (entity.related) unrelated[entity.key] = true
        let name = entity.value.name
        if (!(name in entities)) {
          entities[name] = []
          counts[name] = 1
        } else counts[name]++
        entities[name].push(entity)
      }
      // the maximum number of segments with same name within an utterance
      let maxcounts = {}
      // the newly created entities
      let newentities = []

      for (let field of ['inputs', 'answers']) {
        for (let ut of this[field].list.filter(x => !x.temporary)) {
          // the entity associated to each segment
          let utentities = {}
          // the number of segment for each name
          let utcounts = {}
          // the entity id of each segment
          let utrefs = {}

          let done = (seg, entity) => {
            entity.count++
            entity.keys[ut.key] = seg.key
            entity.related = true
            delete unrelated[entity.key]
            utentities[seg.key] = entity
            utrefs[seg.key] = entity.value.entity
          }

          let todo = []
          for (let seg of ut.segments.filter(seg => seg.type === 'entity')) {
            utcounts[seg.text] = (utcounts[seg.text] || 0) + 1
            let entity = ut.entities[seg.key]
            // deal with already associated segments now
            if (entity && seen[entity.key] &&
              entity.value.name === seg.text) done(seg, entity)
            // and others after
            else todo.push(seg)
          }
          for (let seg of todo) {
            let found = false
            // look for another entity with the right name
            for (let entity of entities[seg.text] || []) {
              if (!entity.keys[ut.key]) {
                if (entity.auto === null) entity.auto = true
                found = true
                done(seg, entity)
                break
              }
            }
            if (found) continue
            // or create one
            let entity = {
              key: Math.random() + '',
              value: {
                name: seg.text,
                entity: null,
              },
              auto: true,
              related: true,
              count: 0,
              keys: {},
              active: false,
            }
            if (!(seg.text in entities)) {
              entities[seg.text] = []
              counts[seg.text] = 1
            } else counts[seg.text]++
            entities[seg.text].push(entity)
            newentities.push(entity)
            done(seg, entity)
          }

          ut.entities = utentities
          ut.refs = utrefs
          ut.counts = utcounts
          for (let name in utcounts) {
            maxcounts[name] = Math.max(maxcounts[name] || 0, utcounts[name])
          }
        }
      }
      // the final list of all entities
      let list = []
      for (let entity of this.entities.list.filter(x => !x.temporary)) {
        if (!unrelated[entity.key]) {
          if (entity.auto === null) entity.auto = false
        } else if (!entity.auto) {
          entity.related = false
        // auto entities should be automatically removed
        } else continue
        list.push(entity)
      }
      list.push(...newentities)
      list.push(this.getNewEntity())

      this.entities.list = list
      this.entities.counts = counts
      this.entities.maxcounts = maxcounts

      // apply all the "new" refs
      for (let field of ['inputs', 'answers']) {
        this[field].list.filter(x => !x.temporary).forEach((ut, index) => {
          for (let key in ut.refs) {
            this.$refs[field][index].setRef(key, ut.refs[key])
          }
          delete ut.refs
        })
      }
    },
    // create a new entity
    getNewEntity () {
      return {
        key: Math.random() + '',
        value: {
          name: '',
          value: '',
          entity: null,
          required: false,
        },
        auto: null,
        related: false,
        count: 0,
        keys: {},
        active: false,
        temporary: true,
      }
    },
    // change an entity entityid
    setEntityRef (entity, value) {
      entity.value.entity = value
      for (let field of ['inputs', 'answers']) {
        this[field].list.filter(x => !x.temporary).forEach((ut, index) => {
          let key = entity.keys[ut.key]
          if (key) this.$refs[field][index].setRef(key, value)
        })
      }
    },
    // change an entity name
    setEntityName (entity, value) {
      entity.value.name = value
      let counts = {}
      for (let other of this.entities.list.filter(x => !x.temporary)) {
        let name = other.value.name
        counts[name] = (counts[name] || 0) + 1
      }
      this.entities.counts = counts
      for (let field of ['inputs', 'answers']) {
        this[field].list.filter(x => !x.temporary).forEach((ut, index) => {
          let key = entity.keys[ut.key]
          if (key) this.$refs[field][index].setText(key, value)
        })
      }
    },
    // entity entityid has been changed from UtteranceEditor
    updateEntityRef (entity, value) {
      entity.value = { ...entity.value, entity: value }
      this.setEntityRef(entity, value)
    },
    // entity name has been changed from UtteranceEditor
    updateEntityName (entity, value) {
      entity.value = { ...entity.value, name: value }
      this.setEntityName(entity, value)
    },
    // move entity
    handleEntityMove (ifrom, ito) {
      let item = this.entities.list.splice(ifrom, 1)[0]
      this.entities.list.splice(ito, 0, item)
    },
    // delete entity
    handleEntityDelete (entity, index) {
      // needs to migrate associated segments to another entity with same name
      if (entity.related) {
        let name = entity.value.name
        let replacements = this.entities.list
          .filter(x => !x.temporary && x.value.name === name && x !== entity)
        for (let field of ['inputs', 'answers']) {
          this[field].list.filter(x => !x.temporary).forEach((ut, index) => {
            let key = entity.keys[ut.key]
            if (!key) return
            for (let rep of replacements) {
              if (rep.keys[ut.key]) continue
              ut.entities[key] = rep
              this.$refs[field][index].setRef(key, rep.value.entity)
              rep.keys[ut.key] = key
              break
            }
          })
        }
      }
      this.entities.list.splice(index, 1)
    },

    // updates inputs.list or answers.list from viewObj.inputs or viewObj.answers
    updateUtterances (field, list, levels) {
      // check that the utterances are different
      levels = levels || {}
      let old = this[field].list.filter(x => !x.temporary)
      let same = !!this[field].list.length
      same = same && old.length === list.length
      let keys = {}
      if (same) {
        old.forEach((ut, i) => {
          keys[ut.value] = ut
          if (ut.value !== list[i] || ut.level !== (levels[i + ''] || 0)) {
            same = false
          }
        })
      }
      // update the list
      if (!same) {
        this[field].list = list.map((val, i) => {
          if (keys[val]) {
            // keys[val].level = levels[i + ''] || 0
            this.$set(keys[val], 'level', levels[i + ''] || 0)
            return keys[val]
          }
          return {
            // a unique key for v-for
            key: Math.random() + '',
            // the real value
            value: val,
            // the level of the input
            level: levels[i + ''] || 0,
            // the list of segment, given by <el-utterance-editor>
            segments: undefined,
            // the entities associated to each segment
            entities: {},
            // how many times each variable name appears
            counts: {},
            // if should be opened
            active: false,
          }
        })
        this[field].list.push(this.getNewUtterance())
      }
    },
    updateInputs (list, levels) { return this.updateUtterances('inputs', list, levels) },
    updateAnswers (list) { return this.updateUtterances('answers', list) },
    // create new input or answer
    getNewUtterance () {
      return {
        key: Math.random() + '',
        value: '',
        level: 0,
        // focus: true,
        segments: undefined,
        entities: {},
        counts: {},
        active: false,
        temporary: true,
      }
    },
    // delete input or answer
    handleUtteranceDelete (field, utterance, index) {
      this[field].list.splice(index, 1)
      // update maxcounts
      let maxcounts = {}
      for (let field of ['inputs', 'answers']) {
        for (let ut of this[field].list.filter(x => !x.temporary)) {
          for (let name in ut.counts) {
            maxcounts[name] = Math.max(maxcounts[name] || 0, ut.counts[name])
          }
        }
      }
      // update entities keys
      let todo = null
      for (let entity of this.entities.list.filter(x => !x.temporary)) {
        delete entity.keys[utterance.key]
        let empty = true
        for (let _ in entity.keys) {
          empty = false
          break
        }
        if (!empty) continue
        // delete auto entity
        if (entity.auto) {
          if (!todo) todo = {}
          todo[entity.key] = true
          this.entities.counts[entity.value.name]--
          if (this.entities.counts[entity.value.name] <= 0) {
            delete this.entities.counts[entity.value.name]
          }
          // nothing more to do because no relation left
        // remove related
        } else {
          entity.related = false
        }
      }
      // delete auto entity
      if (todo) {
        this.entities.list = this.entities.list.filter(x => x.temporary || !todo[x.key])
      }
    },
    handleInputDelete (ut, index) {
      return this.handleUtteranceDelete('inputs', ut, index)
    },
    handleAnswerDelete (ut, index) {
      return this.handleUtteranceDelete('answers', ut, index)
    },

    // update outputs.list form viewObj.outputs
    updateOutputs (outputs) {
      // check if outputs are different
      let same = !!this.outputs.list.length
      same = same && this.computedOutputs.length === outputs.length
      if (same) {
        this.computedOutputs.forEach((op, i) => {
          if (op !== outputs[i]) {
            same = false
          }
        })
      }
      // create new outputs.list
      if (!same) {
        this.outputs.list = outputs.map((val) => {
          return {
            key: Math.random() + '',
            value: val,
            active: false,
          }
        })
        this.outputs.list.push(this.getNewOutput())
      }
    },
    // create a new output context
    getNewOutput () {
      return {
        key: Math.random() + '',
        value: {
          context: '',
        },
        active: false,
        temporary: true,
      }
    },
    // move output context
    handleOutputMove (ifrom, ito) {
      let item = this.outputs.list.splice(ifrom, 1)[0]
      this.outputs.list.splice(ito, 0, item)
    },
    // delete output context
    handleOutputDelete (key) {
      for (let i = 0; i < this.outputs.list.length; i++) {
        if (this.outputs.list[i].key === key) {
          this.outputs.list.splice(i, 1)
          return
        }
      }
      throw new TypeError('key "' + key + '" not found')
    },

    // updates actions.list from viewObj.actions
    updateActions (actions) {
      // check if actions are different
      let same = this.actions.list.length === actions.length
      if (same) {
        this.actions.list.forEach((op, i) => {
          if (op.value !== actions[i]) {
            same = false
          }
        })
      }
      // create a new actions list
      if (!same) {
        this.actions.list = actions.map((val) => {
          return {
            key: Math.random() + '',
            value: val,
            active: false,
          }
        })
      }
    },
    // delete action
    handleActionDelete (key) {
      for (let i = 0; i < this.actions.list.length; i++) {
        if (this.actions.list[i].key === key) {
          this.actions.list.splice(i, 1)
          return
        }
      }
      throw new TypeError('key "' + key + '" not found')
    },
    // move action
    handleActionMove (ifrom, ito) {
      let item = this.actions.list.splice(ifrom, 1)[0]
      this.actions.list.splice(ito, 0, item)
    },
    // create a new action
    addAction (type) {
      let value = { type: type }
      let action = {
        key: Math.random() + '',
        focus: true,
        value: value,
        active: false,
      }
      if (type === 'message') {
        value.language = 'vi-VN'
        value.texts = []
      } else if (type === 'audio' || type === 'image') value.urls = []
      else if (type === 'script') value.script = ''
      else if (type === 'api') {
        value.variable = 'response'
      }
      this.actions.list.push(action)
    },

    // updates follow.list from viewObj.follow
    updateFollow (follow) {
      // check if follow are the same
      let length = this.follow.list.length
      let last = length && this.follow.list[length - 1].value
      last = last && !last.condition && !last.skill && !last.intent &&
        !last.exit && !last.speech
      let same = follow.length === length - last
      if (same) {
        follow.forEach((val, i) => {
          if (val !== this.follow.list[i].value) {
            same = false
          }
        })
      }
      // create a new follow list
      if (!same) {
        this.follow.list = follow.map((val) => {
          return {
            key: Math.random() + '',
            value: val,
            active: false,
          }
        })
      }
      // remove the last empty follow
      if (same && last) {
        if (follow.length && !follow[follow.length - 1].condition) {
          this.follow.list.pop()
        }
      // or create a new one
      } else if (!follow.length || follow[follow.length - 1].condition) {
        this.follow.list.push({
          key: Math.random() + '',
          value: {
            condition: '',
            skill: '',
            intent: '',
            follow: false,
            variables: {},
          },
          active: false,
        })
      }
    },
    // delete a follow
    handleFollowDelete (key) {
      for (let i = 0; i < this.follow.list.length; i++) {
        if (this.follow.list[i].key === key) {
          this.follow.list.splice(i, 1)
          return
        }
      }
      throw new TypeError('key "' + key + '" not found')
    },
    // move a follow
    handleFollowMove (ifrom, ito) {
      let item = this.follow.list.splice(ifrom, 1)[0]
      this.follow.list.splice(ito, 0, item)
    },

    // updates training.examples and training.slots from viewObj.training
    updateTraining (val) {
      val = val || {}
      let old = this.computedTraining || {}
      let same = !!this.training.examples.length
      // check if the list of examples has changed
      let oldExamples = old.examples || []
      let newExamples = val.examples || []
      same = same && oldExamples.length === newExamples.length
      if (same) {
        for (let i = 0; i < oldExamples.length; i++) {
          same = same && oldExamples[i] === newExamples[i]
        }
      }
      // check if the dict of slots has changed
      let oldSlots = old.slots || {}
      let newSlots = val.slots || {}
      for (let slot in oldSlots) {
        if (!slot) continue
        same = same && oldSlots[slot] === newSlots[slot]
      }
      for (let slot in newSlots) {
        if (!slot) continue
        same = same && oldSlots[slot] === newSlots[slot]
      }
      // everything the same
      if (same) return
      // create a new list of examples
      let examples = newExamples.map(ex => {
        return {
          // a unique key for v-for
          key: Math.random() + '',
          // the real value
          value: ex,
          // the list of segment, given by <el-example-editor>
          segments: undefined,
          // the slots associated to each segment
          slots: {},
          // if the example should be considered as valid
          valid: true,
          // if should be opened
          active: false,
        }
      })
      examples.push(this.getNewExample())
      // create a new list of slots
      let slotNames = []
      for (let slot in newSlots) {
        if (!slot) continue
        slotNames.push(slot)
      }
      slotNames.sort()
      let slots = slotNames.map((slot, index) => {
        return {
          // a unique key for v-for
          key: Math.random() + '',
          // the real value
          value: {
            name: slot,
            entity: newSlots[slot],
            key: null,
          },
          // the keys of all the associated segments
          keys: {},
          // the color to use
          color: index,
          cssColor: int2color(index),
          // if the slot should be considered as valid
          valid: true,
          // if should be opened
          active: false,
        }
      })
      // save those
      this.training.slots = slots
      this.training.examples = examples
    },
    validateTraining (callback) {
      let promise = callback ? undefined : new Promise((resolve, reject) => {
        callback = errors => errors ? reject(errors) : resolve()
      })
      let validate = (valid, errors) => {
        if (this.trainingValid) callback()
        else {
          console.log('validateTraining', !valid ? errors : JSON.stringify([
            ...this.training.examples.filter(x => !(x.temporary || x.valid)),
            ...this.training.slots.filter(x => !(x.temporary || x.valid)),
          ]))
          callback(new Error('invalid training'))
        }
      }
      let form = this.$refs.trainingForm
      if (form) {
        form.validate((...args) => this.$nextTick(() => validate(...args)))
      } else {
        validate(true)
      }
      return promise
    },
    getNewExample () {
      return {
        key: Math.random() + '',
        value: '',
        segments: undefined,
        slots: {},
        temporary: true,
        valid: true,
        active: false,
      }
    },
    // update the slots from the segments of all the examples
    // called when training.update is set to true
    updateSegmentSlots () {
      // all slots for each name and key
      let slotsByKey = {}
      let slotsByName = {}
      for (let slot of this.training.slots) {
        slotsByKey[slot.key] = slot
        if (!slotsByName[slot.value.name]) {
          slotsByName[slot.value.name] = slot
        }
        slot.keys = {}
      }
      // comapre with all the segments
      let newSlots = []
      let seenSlots = {}
      let seenColors = {}
      let segColors = {}
      this.training.examples.filter(x => !x.temporary).forEach((ex, i) => {
        let slots = {}
        let colors = {}
        for (let seg of ex.segments.filter(seg => seg.type === 'slot' && seg.name)) {
          // find slot by key
          let slot = ex.slots[seg.key] && slotsByKey[ex.slots[seg.key]]
          // find slot by name
          if (!slot || slot.value.name !== seg.name) {
            slot = slotsByName[seg.name]
          }
          // this old slot is still used
          if (slot) {
            seenSlots[slot.key] = true
            seenColors[slot.color] = true
            if (seg.color !== slot.cssColor) {
              colors[seg.key] = slot.cssColor
            }
          // create a new slot
          } else {
            slot = {
              value: {
                name: seg.name,
                entity: '',
                key: null,
              },
              keys: {},
              key: Math.random() + '',
              valid: true,
              active: false,
            }
            slotsByName[seg.name] = slot
            newSlots.push(slot)
          }
          // save each other key
          if (!slot.keys[ex.key]) {
            slot.keys[ex.key] = []
          }
          slot.keys[ex.key].push(seg.key)
          slots[seg.key] = slot.key
        }
        ex.slots = slots
        for (let _ in colors) {
          segColors[i] = colors
        }
      })
      // check if the list of slots has changed in any way
      let same = !newSlots.length
      let slots = []
      for (let slot of this.training.slots) {
        if (seenSlots[slot.key]) {
          slots.push(slot)
        } else {
          same = false
        }
      }

      if (!same) {
        // assign free colors to the new slots
        newSlots.sort((a, b) => (a.value.name > b.value.name) - (a.value.name < b.value.name))
        let color = 0
        newSlots.forEach(slot => {
          while (seenColors[color]) color++
          slot.color = color
          slot.cssColor = int2color(color)
          color++
          this.training.examples.filter(x => !x.temporary).forEach((ex, i) => {
            for (let key of slot.keys[ex.key] || []) {
              if (ex.slots[key] && ex.slots[key] === slot.key) {
                if (!segColors[i]) segColors[i] = {}
                segColors[i][key] = slot.cssColor
              }
            }
          })
        })
        // save the new slots
        slots.push(...newSlots)
        this.training.slots = slots
      }

      for (let i in segColors) {
        this.$refs.examples[i].setColors(segColors[i])
      }
    },
    // Change the name of a slot for all examples
    setSlotName (slot, name) {
      slot.value.name = name
      this.training.examples.filter(x => !x.temporary).forEach((ex, i) => {
        for (let key of slot.keys[ex.key] || []) {
          if (ex.slots[key] && ex.slots[key] === slot.key) {
            this.$refs.examples[i].setName(key, name)
          }
        }
      })
    },
    // delete an example
    handleExampleDelete (example, index) {
      this.training.examples.splice(index, 1)
      // update slots keys
      let todo = null
      for (let slot of this.training.slots) {
        delete slot.keys[example.key]
        let empty = true
        for (let _ in slot.keys) {
          empty = false
          break
        }
        if (empty) {
          if (!todo) todo = {}
          todo[slot.key] = true
        }
      }
      // remove the slots which have disappeared
      if (todo) {
        this.training.slots = this.training.slots.filter(slot => !todo[slot.key])
      }
    },
    // remove a slot because another one with the same name already exists
    handleSlotDelete (slot, index) {
      this.training.slots.splice(index, 1)
      // reassign slots in examples
      this.training.update = true
    },

    updateSlotsEntities () {
      let entityByKey = {}
      let entityByName = {}
      for (let entity of this.entities.list) {
        entityByKey[entity.key] = entity
        if (!entityByName[entity.value.name]) {
          entityByName[entity.value.name] = entity
        }
      }
      for (let slot of this.training.slots) {
        if (slot.value.key) {
          let entity = entityByKey[slot.value.key]
          if (entity) {
            if (slot.value.entity !== entity.value.name) {
              slot.value.entity = entity.value.name
            }
            continue
          } else {
            slot.value.key = null
          }
        }
        if (!slot.value.entity) continue
        let entity = entityByName[slot.value.entity]
        if (entity) {
          slot.value.key = entity.key
        } else {
          slot.value.entity = ''
        }
      }
    },

    // remove the intent
    remove () {
      State.removeIntent(this.skill, this.intentId).then(() => {
        this.$router.push({
          name: 'skill',
          params: { skill: this.skill },
          query: { view: this.view, compare: this.compare },
        })
      }, this.onerror)
    },
    // save the intent
    save () {
      // validate the form
      this.$refs.form.validate((valid, errors) => {
        if (valid) {
          let promise
          // a new intent, create it and save its intentid
          if (this.intentId === 'new') {
            promise = State.createIntent(this.skill, this.viewObj, this.viewObj.intent)
              .then(data => {
                this.$message({
                  message: 'Intent created',
                  type: 'success',
                })
                this.intentId = data.intent
                this.viewObj = data
                this.$router.replace({
                  name: 'intent',
                  params: { skill: this.skill, intent: this.intentId },
                })
              })
          // an existing intent, save it
          } else {
            promise = State.saveIntent(this.skill, this.intentId, this.viewObj)
              .then(data => {
                this.$message({
                  message: 'Intent saved',
                  type: 'success',
                })
                this.viewObj = data
              })
          }
          // the skill has a new intent, update it for all pages
          promise.then(() => {
            return State.getSkill(this.skill, null, true)
          }).catch(this.onerror)
        } else {
          console.error('errors', errors)
          this.$message.error('Please correct the errors and try again')
        }
      })
    },
    // update viewObj with whatever new fields from publishing/approving/...
    updateIntent (data) {
      if (!data || !data.intent) return
      for (let field in data) this.viewObj[field] = data[field]
    },
    // publish the intent
    publish () {
      State.publishIntent(this.skill, this.intentId, this.timestamp).then(data => {
        this.updateIntent(data)
        this.$message({
          message: 'Intent published',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // unpublish the intent
    unpublish () {
      State.unpublishIntent(this.skill, this.intentId, this.published).then(data => {
        this.updateIntent(data)
        this.$message({
          message: 'Intent unpublished',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // restore the published or approved intent
    restore (version) {
      let timestamp = version === 'pub' ? this.published : this.approved
      let name = version === 'pub' ? 'Published' : 'Approved'
      State['restore' + name + 'Intent'](this.skill, this.intentId, timestamp).then(data => {
        this.updateIntent(data)
        this.$message({
          message: name + ' intent restored',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // untest the intent
    untest () {
      State.untestIntent(this.skill, this.intentId).then(() => {
        this.$message({
          message: 'Test cleared',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // test the intent
    test () {
      State.testIntent(this.skill, this.intentId).then(() => {
        this.$message({
          message: 'Intent in test',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // change the view/compare parameters
    changeView (view, compare) {
      this.$router.push({ query: { view: view, compare: compare } })
    },
    // save and close the text inputs editor
    handleInputsEditor ({ inputs, levels }) {
      this.inputs.editor = false
      this.updateInputs(inputs, levels)
    },
    // chenges the hash for the training
    handleTrainingHash () {
      let hash
      if (this.training.editor) hash = '#examples-editor'
      else if (this.training.visible) hash = '#training-editor'
      if (hash) {
        if (this.$route.hash !== hash) {
          this.$router.replace({ query: this.$route.query, hash: hash })
        }
      } else if (this.$route.hash === '#training-editor' || this.$route.hash === '#examples-editor') {
        this.$router.replace({ query: this.$route.query, hash: null })
      }
    },
    // on clicking outside of the dialog
    handleCloseTraining (val) {
      if (val) return
      // close the text editor
      if (this.training.editor) {
        this.$refs.examplesEditor.handleCancel()
      // validate and close the dialog
      } else {
        this.validateTraining()
          .finally(() => { this.training.visible = false })
          .catch(() => {})
      }
    },
    // save and close the text examples editor
    handleExamplesEditor (examples) {
      this.training.editor = false
      this.updateTraining({
        examples: examples,
        slots: this.computedSlots,
      })
    },
    // the title for each type of action
    actionTitle (type) {
      if (type === 'message') return 'Text response'
      else if (type === 'audio') return 'Audio response'
      else if (type === 'image') return 'Image response'
      else if (type === 'script') return 'Script'
      else if (type === 'api') return 'API call'
      else return 'Response'
    },
    // the icon for each type of action
    actionIcon (type) {
      if (type === 'message') return 'el-icon-edit'
      else if (type === 'audio') return 'el-icon-microphone'
      else if (type === 'image') return 'el-icon-picture-outline'
      else if (type === 'script') return 'el-icon-cpu'
      else if (type === 'api') return 'el-icon-guide'
      else return 'Response'
    },
    checkHash () {
      this.inputs.editor = this.$route.hash === '#inputs-editor'
      this.training.editor = this.$route.hash === '#examples-editor'
      this.training.visible = this.$route.hash === '#training-editor' || this.training.editor
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .intent-dialog .el-dialog {
    width: 90%;
    max-width: 700px;
  }

  .intent-level {
    i {
      display: none;
    }
    i.placeholder {
      display: inline;
      opacity: 0;
    }
    i.selected {
      display: inline;
    }
  }
  .intent-level:hover {
    background-color: #ffffff;
    i.placeholder {
      display: none;
    }
    i {
      color: $--color-text-regular;
      cursor: pointer;
      display: inline;
      opacity: 0.2;
    }
    i:hover, i.level-down:hover ~ i.level-down {
      opacity: 1;
    }
  }

</style>
